import React from 'react';
import { 
  LayoutDashboard, 
  Users, 
  UserX, 
  Wallet, 
  Scale, 
  ShoppingBag, 
  FileText, 
  Send, 
  LifeBuoy, 
  ShieldCheck,
  Sliders,
  Leaf,
  ChevronRight,
  Mail,
  Ticket,
  MessageSquare
} from 'lucide-react';

import { AdminRole, ROLE_PERMITTED_VIEWS, ROLE_DISPLAY_NAMES } from '../../types/admin';

export type NavViewId = 
  | 'dashboard' 
  | 'users' 
  | 'deleted_accounts' 
  | 'escrow' 
  | 'vouchers'
  | 'disputes' 
  | 'listings' 
  | 'cms' 
  | 'broadcast' 
  | 'support' 
  | 'audit'
  | 'admin_team'
  | 'admin_chat'
  | 'email_templates'
  | 'profile_settings';

interface SidebarProps {
  activeView: NavViewId;
  onSelectView: (view: NavViewId) => void;
  role?: AdminRole;
  disputeBadgeCount?: number;
  kycBadgeCount?: number;
  ticketBadgeCount?: number;
  deletedAccountsBadgeCount?: number;
}

export const Sidebar: React.FC<SidebarProps> = ({
  activeView,
  onSelectView,
  role = 'super_admin',
  disputeBadgeCount = 2,
  kycBadgeCount = 4,
  ticketBadgeCount = 5,
  deletedAccountsBadgeCount = 3
}) => {
  const permittedViews = ROLE_PERMITTED_VIEWS[role] || ROLE_PERMITTED_VIEWS.super_admin;
  const navItems = [
    {
      id: 'dashboard' as NavViewId,
      label: 'Dashboard',
      icon: LayoutDashboard,
      badge: null,
    },
    {
      id: 'users' as NavViewId,
      label: 'Users & KYC Queue',
      icon: Users,
      badge: kycBadgeCount > 0 ? `${kycBadgeCount} pending` : null,
      badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-500/30'
    },
    {
      id: 'deleted_accounts' as NavViewId,
      label: 'Deleted Accounts Audit',
      icon: UserX,
      badge: deletedAccountsBadgeCount > 0 ? `${deletedAccountsBadgeCount}` : null,
      badgeColor: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
      highlight: true
    },
    {
      id: 'escrow' as NavViewId,
      label: 'Escrow & Financial Ledger',
      icon: Wallet,
      badge: 'Live',
      badgeColor: 'bg-brand-500/20 text-brand-300 border-brand-500/30'
    },
    {
      id: 'vouchers' as NavViewId,
      label: 'Escrow Fee Vouchers',
      icon: Ticket,
      badge: 'Coupons',
      badgeColor: 'bg-teal-500/20 text-teal-300 border-teal-500/30'
    },
    {
      id: 'disputes' as NavViewId,
      label: 'Dispute Tribunal',
      icon: Scale,
      badge: disputeBadgeCount > 0 ? `${disputeBadgeCount}` : null,
      badgeColor: 'bg-purple-500/20 text-purple-300 border-purple-500/30'
    },
    {
      id: 'listings' as NavViewId,
      label: 'Listing Moderation',
      icon: ShoppingBag,
      badge: null,
    },
    {
      id: 'cms' as NavViewId,
      label: 'CMS & Dynamic Pages',
      icon: FileText,
      badge: 'Pages',
      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
    },
    {
      id: 'broadcast' as NavViewId,
      label: 'Broadcast Push Sender',
      icon: Send,
      badge: null,
    },
    {
      id: 'support' as NavViewId,
      label: 'Support Ticket Desk',
      icon: LifeBuoy,
      badge: ticketBadgeCount > 0 ? `${ticketBadgeCount}` : null,
      badgeColor: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
    },
    {
      id: 'audit' as NavViewId,
      label: 'Security Audit Logs',
      icon: ShieldCheck,
      badge: null,
    },
    {
      id: 'admin_team' as NavViewId,
      label: 'Admin Team & Governance',
      icon: Sliders,
      badge: 'RBAC',
      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
    },
    {
      id: 'admin_chat' as NavViewId,
      label: 'Admin Team Chat',
      icon: MessageSquare,
      badge: 'Staff',
      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
    },
    {
      id: 'email_templates' as NavViewId,
      label: 'Email Templates (SMTP)',
      icon: Mail,
      badge: 'ZeptoMail',
      badgeColor: 'bg-teal-500/20 text-teal-300 border-teal-500/30'
    },
    {
      id: 'profile_settings' as NavViewId,
      label: 'Profile & 2FA Security',
      icon: ShieldCheck,
      badge: '2FA',
      badgeColor: 'bg-brand-500/20 text-brand-300 border-brand-500/30'
    },
  ];

  return (
    <aside className="w-72 shrink-0 flex flex-col justify-between h-screen sticky top-0 transition-colors border-r bg-[#10231d] border-[#22493c] text-[#f5f5f0]">
      {/* Top Branding */}
      <div>
        <div className="h-20 flex items-center gap-3.5 px-6 border-b border-[#22493c]">
          <div className="w-10 h-10 rounded-2xl bg-brand-500/20 border border-brand-500/30 flex items-center justify-center shadow-glow-sm">
            <Leaf className="w-5 h-5 text-brand-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-lg tracking-tight text-white">
                ReUseIt
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-500/20 text-brand-400 border border-brand-500/30 uppercase tracking-wider">
                {ROLE_DISPLAY_NAMES[role] || 'Super Admin'}
              </span>
            </div>
            <p className="text-[11px] text-[#90cbb7]">
              The Neon Conservatory Portal
            </p>
          </div>
        </div>

        {/* Navigation list (Strictly filtered by role privileges) */}
        <nav className="p-4 space-y-1.5 overflow-y-auto max-h-[calc(100vh-160px)]">
          {navItems.filter(item => permittedViews.includes(item.id)).map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onSelectView(item.id)}
                className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-2xl text-xs font-semibold transition-all duration-200 group text-left
                  ${isActive 
                    ? 'bg-brand-500 text-forest-void shadow-glow-sm font-bold' 
                    : 'text-[#90cbb7] hover:bg-[#152a23] hover:text-white'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-4 h-4 transition-colors ${isActive ? 'text-forest-void' : 'group-hover:text-brand-400'}`} />
                  <span className="truncate">{item.label}</span>
                </div>

                <div className="flex items-center gap-1.5">
                  {item.badge && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${isActive ? 'bg-forest-void/20 text-forest-void border-forest-void/30' : item.badgeColor}`}>
                      {item.badge}
                    </span>
                  )}
                  {isActive && <ChevronRight className="w-3.5 h-3.5 text-forest-void" />}
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Bottom Environmental Sustainability Footer */}
      <div className="p-4 border-t border-[#22493c]">
        <div className="rounded-2xl p-3 bg-[#152a23] border border-[#22493c]">
          <div className="flex items-center justify-between text-xs">
            <span className="text-[#90cbb7] font-medium">CO2 Diverted</span>
            <span className="text-brand-400 font-bold">1,842.6 kg</span>
          </div>
          <div className="mt-2 w-full bg-[#10231d] rounded-full h-1.5 overflow-hidden">
            <div className="bg-brand-500 h-full rounded-full w-[78%]"></div>
          </div>
          <p className="mt-2 text-[10px] text-[#90cbb7]/80">
            Platform Target: 2,500 kg CO2 / month
          </p>
        </div>
      </div>
    </aside>
  );
};
